import { Fragment, useEffect, useRef, useState } from "react";

import { useTranslation } from "react-i18next";

import classes from "./CallScripts.module.css";
import { ScriptEndType, ScriptModule } from "./callScripts.types";
import { MODULES, NODES } from "./scripts.data";

interface CallScriptsAppProps {
  agentName: string;
  clientName: string;
  onScriptCompleted?: (moduleId: ScriptModule["id"], endType: ScriptEndType) => void;
}

const END_CLASS: Record<ScriptEndType, string> = {
  success: classes.es,
  callback: classes.ec,
  negative: classes.en,
};

const CallScriptsApp = ({ agentName, clientName, onScriptCompleted }: CallScriptsAppProps) => {
  const { t } = useTranslation();
  const [current, setCurrent] = useState<ScriptModule | null>(null);
  const [history, setHistory] = useState<string[]>([]);

  const currentNodeId = history[history.length - 1];
  const currentNode = currentNodeId ? NODES[currentNodeId] : undefined;

  // Keep the latest callback in a ref so the completion effect can depend only on
  // the reached node — not on the callback identity, which would re-fire it.
  const onScriptCompletedRef = useRef(onScriptCompleted);
  onScriptCompletedRef.current = onScriptCompleted;

  // Fire completion once when the agent reaches an end node.
  useEffect(() => {
    if (current && currentNode?.end && currentNode.endType) {
      onScriptCompletedRef.current?.(current.id, currentNode.endType);
    }
  }, [current, currentNode]);

  const interpolation = { agentName, clientName };

  const startModule = (id: ScriptModule["id"]) => {
    const module = MODULES.find((m) => m.id === id);
    if (!module) {
      return;
    }
    setCurrent(module);
    setHistory([module.start]);
  };

  const choose = (nodeId: string) => setHistory((prev) => [...prev, nodeId]);

  const goMenu = () => {
    setCurrent(null);
    setHistory([]);
  };

  const goBack = () => {
    if (history.length > 1) {
      setHistory((prev) => prev.slice(0, -1));
    } else {
      goMenu();
    }
  };

  // Multi-line agent/prospect text uses \n / \n\n — render with line breaks.
  const renderText = (text: string) => {
    const lines = text.split("\n");
    return lines.map((line, index) => (
      <Fragment key={index}>
        {line}
        {index < lines.length - 1 ? <br /> : null}
      </Fragment>
    ));
  };

  const nodeText = (nodeId: string, field: string) =>
    t(`callScripts.nodes.${nodeId}.${field}`, interpolation);

  const nodeChoices = (nodeId: string): string[] => {
    const labels = t(`callScripts.nodes.${nodeId}.choices`, { returnObjects: true });
    return Array.isArray(labels) ? (labels as string[]) : [];
  };

  if (!current || !currentNode) {
    return (
      <div className={`${classes.root} ${classes.fu}`}>
        <div className={classes.page}>
          <div className={classes.hero}>
            <h1>{t("callScripts.title")}</h1>
            <p>{t("callScripts.hero")}</p>
          </div>

          <div className={classes.grid}>
            {MODULES.map((module) => (
              <button
                className={classes.mc}
                key={module.id}
                onClick={() => startModule(module.id)}
                type="button"
              >
                <div className={classes.mi}>{module.icon}</div>
                <div className={classes.mb}>
                  <div className={classes.mtitle}>{t(`callScripts.modules.${module.id}.title`)}</div>
                  <div className={classes.msub}>{t(`callScripts.modules.${module.id}.sub`)}</div>
                </div>
                <div className={classes.marrow}>›</div>
              </button>
            ))}
          </div>

          <div className={classes.foot}>{t("callScripts.labels.footer")}</div>
        </div>
      </div>
    );
  }

  const choiceLabels = currentNode.end ? [] : nodeChoices(currentNodeId);

  return (
    <div className={`${classes.root} ${classes.fu}`}>
      <div className={classes.page}>
        <div className={classes.pt}>
          <button className={classes.bbtn} onClick={goBack} type="button">
            {t("callScripts.labels.back")}
          </button>
          <span className={classes.sc}>{t("callScripts.labels.step", { n: history.length })}</span>
        </div>

        <div className={classes.prog}>
          {history.map((nodeId, index) => (
            <div
              className={`${classes.pd} ${
                index === history.length - 1 ? classes.pdActive : classes.pdDone
              }`}
              key={`${nodeId}-${index}`}
            />
          ))}
          {!currentNode.end ? <div className={classes.pd} /> : null}
        </div>

        {history.length > 1 ? (
          <div className={classes.hist}>
            {history.slice(0, -1).map((nodeId, index) => {
              const node = NODES[nodeId];
              const nextId = history[index + 1];
              const choiceIndex = node.choices?.findIndex((choice) => choice.next === nextId) ?? -1;
              const labels = node.choices ? nodeChoices(nodeId) : [];
              const choiceLabel = choiceIndex >= 0 ? labels[choiceIndex] : undefined;

              return (
                <Fragment key={`${nodeId}-${index}`}>
                  {choiceLabel ? (
                    <div className={classes.hi}>
                      <div className={`${classes.ml} ${classes.mlPr}`}>
                        {t("callScripts.labels.prospectShort")}
                      </div>
                      <div className={`${classes.bbl} ${classes.bblPr} ${classes.bblSmall}`}>
                        {choiceLabel}
                      </div>
                    </div>
                  ) : null}
                  <div className={classes.hi}>
                    <div className={`${classes.ml} ${classes.mlAg}`}>
                      {t("callScripts.labels.agentShort")}
                    </div>
                    <div className={`${classes.bbl} ${classes.bblAg} ${classes.bblSmall}`}>
                      {renderText(nodeText(nodeId, "agent"))}
                    </div>
                  </div>
                </Fragment>
              );
            })}
            <hr className={classes.dvd} />
          </div>
        ) : null}

        {currentNode.hasCtx ? (
          <div className={classes.ctx}>📍 {nodeText(currentNodeId, "ctx")}</div>
        ) : null}

        {currentNode.hasProspect ? (
          <div className={classes.block}>
            <div className={`${classes.ml} ${classes.mlPr}`}>{t("callScripts.labels.prospect")}</div>
            <div className={`${classes.bbl} ${classes.bblPr}`}>
              {renderText(nodeText(currentNodeId, "prospect"))}
            </div>
          </div>
        ) : null}

        <div className={classes.blockTight}>
          <div className={`${classes.ml} ${classes.mlAg}`}>{t("callScripts.labels.agent")}</div>
          <div className={`${classes.bbl} ${classes.bblAg}`}>
            {renderText(nodeText(currentNodeId, "agent"))}
          </div>
        </div>

        {currentNode.hasNote ? (
          <div className={classes.note}>
            <span className={classes.noteI}>📋</span>
            <span>{nodeText(currentNodeId, "note")}</span>
          </div>
        ) : null}

        {currentNode.hasTip ? (
          <div className={classes.tip}>
            <span className={classes.tipI}>💡</span>
            <span>{nodeText(currentNodeId, "tip")}</span>
          </div>
        ) : null}

        {currentNode.end && currentNode.endType ? (
          <>
            <div className={`${classes.eb} ${END_CLASS[currentNode.endType]}`}>
              {nodeText(currentNodeId, "endMsg")}
            </div>
            <div className={classes.nr}>
              <button className={`${classes.nb} ${classes.nbO}`} onClick={goBack} type="button">
                {t("callScripts.labels.back")}
              </button>
              <button className={`${classes.nb} ${classes.nbP}`} onClick={goMenu} type="button">
                {t("callScripts.labels.chooseModule")}
              </button>
            </div>
          </>
        ) : (
          <div>
            <div className={classes.cl}>{t("callScripts.labels.choicesPrompt")}</div>
            {currentNode.choices?.map((choice, index) => (
              <button
                className={classes.ch}
                key={`${choice.next}-${index}`}
                onClick={() => choose(choice.next)}
                type="button"
              >
                <span>{choiceLabels[index]}</span>
                <span className={classes.ca}>›</span>
              </button>
            ))}
            {history.length > 1 ? (
              <div className={classes.nr} style={{ marginTop: 14 }}>
                <button className={`${classes.nb} ${classes.nbO}`} onClick={goBack} type="button">
                  {t("callScripts.labels.back")}
                </button>
              </div>
            ) : null}
          </div>
        )}

        <div className={classes.foot}>{t("callScripts.labels.footer")}</div>
      </div>
    </div>
  );
};

export default CallScriptsApp;
